import { defineStore } from 'pinia';
import { i18n } from 'src/boot/i18n';
import type { dynamicObj } from 'src/pages/assets';
import { isObjectEmtpy } from 'src/utils/general';
import { useDocumentStore } from 'src/store/main';

export const useLineItemStore = defineStore('line_item', {
  state: () => ({
    docStore: useDocumentStore(),
    validation_done: true,
    missing_mapping_column: [] as string[],
    tableColumns: [] as any,
    lineItemLayout: [] as any,
    dropdownColumns: [] as any,
    columns_to_hide: [] as any,
    custom_columns_to_hide: [] as any,
    advance_settings_columns: [] as any,
    formatting_options: {} as { [k: string]: any },
    column_mapping_indexes: {} as { [k: string]: any },
    row_errors: false,
    tableRows: [] as any,
    lineDataObj: [] as dynamicObj, //dictionary Obj
  }),
  actions: {
    prepareTableLayoutObj() {
      // remove duplicate
      this.dropdownColumns = [];
      const duplicate_columns = {} as dynamicObj;
      this.lineItemLayout = {} as dynamicObj;
      this.initStandardColumns();

      // logInfo('prepareTableLayoutObj',this.docStore.record.tables);
      if (
        !(
          this.docStore.record.tables &&
          this.docStore.record.tables.length > 0 &&
          this.docStore.record.tables[0].hasOwnProperty('layout')
        )
      ) {
        return;
      }
      // logInfo('prepareTableLayoutObj 2', this.docStore.record.tables[0].layout)
      if (
        !this.docStore.record.tables[0].layout ||
        !Array.isArray(this.docStore.record.tables[0].layout)
      ) {
        console.error(
          'prepareTableLayoutObj: table layout is not available or not an array',
        );
        return;
      }
      Array.from(this.docStore.record.tables[0].layout).forEach(
        (column: any) => {
          if (
            !duplicate_columns.hasOwnProperty(column.column_name) &&
            column.is_hidden == false &&
            !this.isCustomColumnHidden(column.column_name)
          ) {
            duplicate_columns[column.column_name] = true;

            // prepare Coloum layout
            column['lbl_to_display'] = this.getLabelToDisplay(column);
            this.lineItemLayout[column.column_name] = column;

            this.addStandardColumns(column);

            // prepare Coloum Dropdown
            this.dropdownColumns.push({
              header: column.lbl_to_display,
              techName: column.column_name,
            });
          }
        },
      );

      this.sortMappingColumns();
    },
    initStandardColumns() {
      this.advance_settings_columns = [];
      this.advance_settings_columns.push(
        Object.assign({}, { value: '', label: i18n.t('select') }),
      );
      this.advance_settings_columns.push(
        Object.assign({}, { value: '__trash__', label: i18n.t('trash') }),
      );
    },
    isCustomColumnHidden(column_name: string) {
      let hidden = false;
      if (
        !this.custom_columns_to_hide ||
        this.custom_columns_to_hide.length == 0
      ) {
        return hidden;
      }

      for (const column of this.custom_columns_to_hide) {
        if (column === column_name) {
          hidden = true;
          break;
        }
      }

      return hidden;
    },
    getLabelToDisplay(column: any) {
      let label = column.title as string;
      if (
        i18n.t(column.translation_key) !==
        'pages.line_items_columns.' + column.translation_key
      ) {
        label = i18n.t(column.translation_key);
      }
      return label;
    },
    addStandardColumns(column: any) {
      this.advance_settings_columns.push(
        Object.assign(
          {},
          { value: column.column_name, label: column.column_name },
        ),
      );
    },
    sortMappingColumns() {
      if (this.dropdownColumns.length > 0) {
        this.dropdownColumns.sort(function (a: any, b: any) {
          if (a.header < b.header) {
            return -1;
          }
          if (a.header > b.header) {
            return 1;
          }
          return 0;
        });
      }
    },
    addInMapping(selectedIndex: any, column_name: string) {
      this.column_mapping_indexes[column_name] = Number(selectedIndex);
    },
    hasOriginalIndex(index: any, item: any) {
      let hasIndex = false;
      if (index !== '') {
        hasIndex = true;
      }
      return hasIndex;
    },
    validateLineItemColumns() {
      let hasError = false;
      this.docStore.record['table_has_error'] = false;

      if (this.tableColumns === undefined) {
        return;
      }

      for (const column of this.tableColumns) {
        const isValid = this.isColumnNameValid(column);
        if (!isValid) {
          this.docStore.record['table_has_error'] = true;
          column.error = true;
          hasError = true;
          continue;
        }
      }

      const newValidation = !hasError;
      if (this.validation_done === newValidation) {
        return;
      }

      if (!this.validation_done && newValidation) {
        //this.updateLineItemsForApi();
      }

      this.validation_done = newValidation;
    },
    isColumnNameValid(header: any) {
      const isEmptyMapping =
        this.column_mapping_indexes &&
        this.column_mapping_indexes != null &&
        isObjectEmtpy(this.column_mapping_indexes)
          ? true
          : false;
      let isValid = false;

      for (const column of this.dropdownColumns) {
        if (header.name === column.techName) {
          isValid = true;
          break;
        }
      }

      if (
        isValid &&
        isEmptyMapping &&
        this.hasOriginalIndex(header.org_index, header) === true
      ) {
        this.addInMapping(header.org_index, header.name);
      }
      return isValid;
    },
    setFormatOptions() {
      this.formatting_options = {};
      if (
        this.docStore.record.tables &&
        this.docStore.record.tables.length > 0 &&
        this.docStore.record.tables[0].hasOwnProperty('formatting_options')
      ) {
        this.formatting_options =
          this.docStore.record.tables[0].formatting_options;
      }

      if (
        this.formatting_options.hasOwnProperty('mapping') &&
        this.formatting_options.mapping !== null
      ) {
        this.column_mapping_indexes = Object.assign(
          {},
          this.formatting_options.mapping,
        );
      }

      if (
        this.formatting_options.hasOwnProperty('columns_to_hide') &&
        this.formatting_options.columns_to_hide !== null
      ) {
        this.columns_to_hide = Object.assign(
          [],
          this.formatting_options.columns_to_hide,
        );
      }
      if (
        this.formatting_options.hasOwnProperty('custom_columns_to_hide') &&
        this.formatting_options.custom_columns_to_hide !== null
      ) {
        this.custom_columns_to_hide = Object.assign(
          [],
          this.formatting_options.custom_columns_to_hide,
        );
      }
    },
    isColumnVisible(index: string, column: any) {
      let visbile = true;
      if (!index || !this.columns_to_hide || this.columns_to_hide.length == 0) {
        return visbile;
      }

      if (this.columns_to_hide.includes(index) || column.name == 'UUID') {
        visbile = false;
      }

      return visbile;
    },
    isColumnHidden(row: any, column: any) {
      let org_index = '';
      if (column.org_index != null && column.org_index != undefined) {
        org_index = String(column.org_index);
      }

      let allowed = true;
      if (org_index !== '') {
        allowed = this.isColumnVisible(org_index, column);
      }

      if (org_index === '') {
        allowed = !this.isCustomColumnHidden(column.name);
      }
      return allowed;
    },

    // this method is only for screen validation
    prepareTableErrors() {
      this.prepareTableLayoutObj();
      this.setFormatOptions();
      this.tableColumns = [];
      this.row_errors = false;

      const added_columns = {} as dynamicObj;
      if (
        !(
          this.docStore.record.tables &&
          this.docStore.record.tables.length > 0 &&
          this.docStore.record.tables[0].hasOwnProperty('rows') &&
          this.docStore.record.tables[0].rows.length > 0
        )
      ) {
        return;
      }
      // loop through this.docStore.record.tables[0].rows
      for (const row of this.docStore.record.tables[0].rows) {
        for (const index in row.messages) {
          const data = row.messages[index];
          if (!data.message || data.message == '') {
            continue;
          }

          this.row_errors = true;
          break;
        }

        Array.from(row.columns).forEach((column: any) => {
          const allowed = this.isColumnHidden(row, column);
          if (allowed && !added_columns.hasOwnProperty(column.name)) {
            added_columns[column.name] = true;
            const label = column.name;

            let org_index = '';
            if (column.org_index != null && column.org_index != undefined) {
              org_index = String(column.org_index);
            }

            const columnItem = {
              name: column.name,
              field: column.name,
              align: 'left',
              label: label,
              is_mapped: column.is_mapped ? column.is_mapped : false,
              org_index: org_index !== '' ? org_index : '',
            };

            this.tableColumns.push(columnItem);
          }
        });
      }

      if (this.row_errors) {
        this.docStore.record['table_has_error'] = true;
        return;
      }
      this.validateLineItemColumns();
    },
  },
});
